import { apiClient } from '@/lib/api/client'

export type ContentType = 'room_collection' | 'artwork_editorial' | 'artist_editorial' | 'easel_artwork_editorial' | 'blog_editorial' | 'mockup' | 'gallery_set' | 'gallery_wall_set' | 'shoppable_room' | 'hero_asset' | 'recommendations'

/** The brief a curator writes to commission a Journal article. */
export interface BlogBrief {
  angle: string
  artist?: string
  title?: string
  doResearch?: boolean
}

export interface BlogGenerateResult {
  queued: boolean
}
export type ContentStatus = 'draft' | 'review' | 'active' | 'seasonal' | 'archived' | 'rejected'
export type ContentTransition = 'approve' | 'review' | 'reject' | 'archive'

// ─── Mockup (image) content type ─────────────────────────
export type MockupKind = 'template' | 'scene'

export interface MockupSlide {
  kind: MockupKind
  url: string
  alt?: string
  room?: string | null
  mood?: string | null
  style?: string
  width?: number
  height?: number
  aspect?: number
  accepted?: boolean // curator toggle; undefined/true = published
}

export interface MockupSlot {
  kind: MockupKind
  room?: string
  frame?: string
  aspect?: string
  alt?: string
}

export interface MockupArtwork {
  cdnId?: string
  url?: string
  title?: string
  handle?: string
}

export interface GenerateMockupsRequest {
  handle?: string
  artwork?: MockupArtwork
  /** Render-target id (a consuming surface's style + display constraints). */
  target?: string
  slots?: MockupSlot[]
  style?: { kind: string; prompt?: string; artist?: unknown; artistHandle?: string }
  rooms?: string[]
  templateRoom?: string
  frame?: string
}

export interface MockupTarget {
  id: string
  label: string
  surface: string
  style: { kind: string; artistHandle?: string }
  display: { aspect?: string | null; fit?: string; sceneAspect?: string | null; templateAspect?: string | null }
}

export interface ContentItem {
  id: string
  contentType: ContentType
  targetRef: string | null
  targetUrl: string | null
  title: string
  status: ContentStatus
  confidence: number | null
  source: 'agent' | 'human' | 'script'
  generatedBy: string | null
  payload: Record<string, unknown>
  violations: unknown[]
  assignedTo: number | null
  assignedName?: string | null
  idempotencyKey: string | null
  createdAt: string
  updatedAt: string
  reviewedAt: string | null
  publishedAt: string | null
  archivedAt: string | null
  metadata: Record<string, unknown>
}

export interface ContentAction {
  id: string
  itemId: string
  actor: 'agent' | 'curator' | 'system'
  actorId: number | null
  actorName?: string | null
  action: string
  before: unknown
  after: unknown
  note: string | null
  createdAt: string
}

export interface ContentStats {
  total: number
  byType: Record<string, Record<string, number>>
}

export interface ContentListParams {
  type?: ContentType
  status?: ContentStatus
  assignedTo?: number
  q?: string
  limit?: number
  offset?: number
}

export interface ContentListResponse {
  data: ContentItem[]
  count: number
}

// ─── Recommendations (PDP related-dimensions) coverage ───
export interface RecommendationCoverage {
  covered: number
  totalProducts: number | null
  /**
   * False when Shopify's product count saturated (it caps at 10,000 and reports AT_LEAST), so
   * `totalProducts` is a floor rather than the catalog size. A percentage against a floor is
   * meaningless, which is why the page stops showing one.
   */
  totalProductsExact: boolean | null
}
export interface RecommendationProduct { handle: string; title: string; image: string; covered: boolean }
export interface RecommendationProductsResponse {
  items: RecommendationProduct[]
  pageInfo: { hasNextPage: boolean; cursor: string | null }
}

export const contentApi = {
  getStats: async (): Promise<ContentStats> => {
    const res = await apiClient.get<ContentStats>('/api/content/stats')
    return res.data
  },

  getItems: async (params: ContentListParams = {}): Promise<ContentListResponse> => {
    const res = await apiClient.get('/api/content/items', {
      params: {
        type: params.type,
        status: params.status,
        assignedTo: params.assignedTo,
        q: params.q,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    })
    const count = parseInt(res.headers['count'] || '0', 10)
    return { data: res.data, count }
  },

  createAcademyDraft: async (): Promise<ContentItem> => {
    const res = await apiClient.post<ContentItem>('/api/content/academy/drafts', {})
    return res.data
  },

  getItem: async (id: string): Promise<ContentItem> => {
    const res = await apiClient.get<ContentItem>(`/api/content/items/${id}`)
    return res.data
  },

  updateItem: async (id: string, data: Partial<ContentItem>): Promise<ContentItem> => {
    const res = await apiClient.patch<ContentItem>(`/api/content/items/${id}`, data)
    return res.data
  },

  transition: async (id: string, action: ContentTransition, note?: string): Promise<ContentItem> => {
    const res = await apiClient.post<ContentItem>(`/api/content/items/${id}/${action}`, { note })
    return res.data
  },

  getActions: async (id: string): Promise<ContentAction[]> => {
    const res = await apiClient.get<ContentAction[]>(`/api/content/items/${id}/actions`)
    return res.data
  },

  // ─── Mockup generation ─────────────────────────────────
  getMockupTargets: async (): Promise<MockupTarget[]> => {
    const res = await apiClient.get<MockupTarget[]>('/api/content/mockup/targets')
    return res.data
  },

  generateMockups: async (req: GenerateMockupsRequest): Promise<ContentItem> => {
    const res = await apiClient.post<ContentItem>('/api/content/mockup/generate', req)
    return res.data
  },

  generateMockupSlot: async (req: { artwork: MockupArtwork; slot: MockupSlot; style?: unknown }): Promise<MockupSlide> => {
    const res = await apiClient.post<MockupSlide>('/api/content/mockup/slot', req)
    return res.data
  },

  // ─── Recommendations coverage + actions ────────────────
  getRecommendationCoverage: async (): Promise<RecommendationCoverage> => {
    const res = await apiClient.get<RecommendationCoverage>('/api/content/recommendations/coverage')
    return res.data
  },

  getRecommendationProducts: async (
    params: { filter?: 'all' | 'missing' | 'covered'; cursor?: string | null; limit?: number } = {}
  ): Promise<RecommendationProductsResponse> => {
    const res = await apiClient.get<RecommendationProductsResponse>('/api/content/recommendations/products', {
      params: { filter: params.filter ?? 'all', cursor: params.cursor ?? undefined, limit: params.limit ?? 50 },
    })
    return res.data
  },

  generateRecommendation: async (handle: string, title?: string): Promise<{ handle: string; lanes: number }> => {
    const res = await apiClient.post('/api/content/recommendations/generate', { handle, title })
    return res.data
  },

  enqueueRecommendations: async (handles: string[]): Promise<{ enqueued: number; failed: number }> => {
    const res = await apiClient.post('/api/content/recommendations/enqueue', { handles })
    return res.data
  },

  // ─── Generic content coverage (all content types) ──────
  getContentTypes: async (): Promise<{ id: ContentType; label: string; kind: string }[]> => {
    const res = await apiClient.get('/api/content/coverage/types')
    return res.data
  },
  getContentSummary: async (type: ContentType): Promise<{ type: ContentType; kind: string; totalTargets: number | null }> => {
    const res = await apiClient.get('/api/content/coverage/summary', { params: { type } })
    return res.data
  },
  getContentTargets: async (
    type: ContentType, params: { filter?: 'all' | 'missing' | 'covered'; cursor?: string | null; limit?: number } = {}
  ): Promise<RecommendationProductsResponse> => {
    const res = await apiClient.get('/api/content/coverage/targets', {
      params: { type, filter: params.filter ?? 'all', cursor: params.cursor ?? undefined, limit: params.limit ?? 50 },
    })
    return res.data
  },
  generateContent: async (type: ContentType, target_ref: string): Promise<Record<string, unknown>> => {
    const res = await apiClient.post('/api/content/coverage/generate', { type, target_ref })
    return res.data
  },

  /**
   * Commission a Journal article. Returns as soon as the work is queued — writing runs
   * research + generation + the quality gate and takes ~60s, past the 60s ceiling on
   * Firebase Hosting's rewrite to Cloud Run. Holding the request open returned a browser
   * error on work the server completed, and a retry commissioned the article twice.
   */
  generateBlogArticle: async (brief: BlogBrief): Promise<BlogGenerateResult> => {
    const res = await apiClient.post<BlogGenerateResult>('/api/content/blog/generate', brief)
    return res.data
  },
  enqueueContent: async (type: ContentType, handles: string[]): Promise<{ enqueued: number; failed: number }> => {
    const res = await apiClient.post('/api/content/coverage/enqueue', { type, handles })
    return res.data
  },
}
